/**
 * UI module: start screen, AR HUD, settings/debug sheets, toasts, map slots.
 *
 * Everything is rendered inside `overlayRoot` (pass it as the WebXR dom-overlay root).
 * Plain DOM + CSS, no framework. See dev/ui.html for the harness.
 */
import './ui.css';
import type { HudState, UiCallbacks } from '../contracts';
import { DebugPanel } from './debug-panel';
import { guardXrSelect, h, setClass } from './dom';
import { nextCumulativeRotation, normDeg } from './format';
import { Hud } from './hud';
import { SettingsPanel } from './settings-panel';
import { StartScreen, type StartPhase } from './start-screen';
import { Toasts, type ToastKind } from './toast';

export type { StartPhase } from './start-screen';
export type { ToastKind } from './toast';

export interface UiController {
  /** Root element of the whole UI; use as WebXR `domOverlay.root`. */
  readonly overlayRoot: HTMLElement;
  /** Mount points for MapView elements: `mini` (bottom corner) and `full` (fullscreen layer). */
  readonly mapSlots: { mini: HTMLElement; full: HTMLElement };
  showStart(): void;
  showSession(): void;
  setStartPhase(phase: StartPhase, message?: string): void;
  /** Call every animation frame while in session. Transforms per frame, text at <= 2 Hz. */
  updateHud(state: HudState): void;
  /** Show/hide the fullscreen map layer (content is mounted by the integration into mapSlots.full).
   *  Resolves once the grow/shrink animation has finished (move the map content back after close). */
  setMapOpen(open: boolean): Promise<void>;
  readonly isMapOpen: boolean;
  openSettings(): void;
  closeSettings(): void;
  toast(text: string, kind?: ToastKind): void;
  /** Currently applied HUD rotation in degrees (0/90/180/270). */
  readonly hudRotationDeg: number;
  destroy(): void;
}

export function createUi(root: HTMLElement, callbacks: UiCallbacks): UiController {
  const overlay = h('div', 'uz-ui', { lang: 'de-CH' });
  const rot = h('div', 'uz-rot', { 'data-rot': '0' });
  overlay.append(rot);
  guardXrSelect(overlay);

  let inSession = false;
  let rotTarget = 0;
  let rotCum = 0;
  let mapOpen = false;

  const toasts = new Toasts();
  const toast = (text: string, kind?: ToastKind) => toasts.show(text, kind);

  const settings: SettingsPanel = new SettingsPanel({
    toast,
    openDebug: () => {
      settings.sheet.hide();
      debug.open(null);
    },
  });
  const debug: DebugPanel = new DebugPanel({
    callbacks,
    toast,
    isSession: () => inSession,
    backToSettings: () => {
      debug.sheet.hide();
      settings.sheet.open(); // keeps the original opener for focus restore
    },
  });

  const openSettings = (opener?: HTMLElement | null) => {
    debug.sheet.hide();
    settings.open(opener ?? null);
  };
  const closeSheets = () => {
    debug.sheet.close();
    settings.sheet.close();
  };

  const hud = new Hud({
    callbacks,
    openSettings: (opener) => openSettings(opener),
  });
  // Settings (and the debug sheet) are only reachable once a session is running.
  const start = new StartScreen(callbacks);

  const mapFull = h('div', 'uz-map-full', { hidden: true, role: 'dialog', 'aria-label': 'Karte' });
  guardXrSelect(mapFull);

  rot.append(hud.el, mapFull, start.el, settings.sheet.el, debug.sheet.el, toasts.el);
  overlay.dataset.view = 'start';
  root.append(overlay);

  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    if (debug.sheet.isOpen) debug.sheet.close();
    else if (settings.sheet.isOpen) settings.sheet.close();
  };
  document.addEventListener('keydown', onKey);

  function applyRotation(deg: number): void {
    const target = normDeg(Math.round(normDeg(deg) / 90) * 90);
    if (target === rotTarget) return;
    rotTarget = target;
    rotCum = nextCumulativeRotation(rotCum, target);
    rot.dataset.rot = String(target);
    setClass(rot, 'is-swapped', target === 90 || target === 270);
    rot.style.transform = `translate(-50%, -50%) rotate(${rotCum}deg)`;
    // Short fade hides the instant width/height swap while the rotation animates.
    if (typeof rot.animate === 'function' && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      rot.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
    }
  }

  /** Layout rect of `el` relative to `ancestor`, from offsets (unaffected by the HUD rotation). */
  function layoutRect(el: HTMLElement, ancestor: HTMLElement): { x: number; y: number; w: number; h: number } | null {
    let x = 0;
    let y = 0;
    let n: HTMLElement | null = el;
    while (n && n !== ancestor) {
      x += n.offsetLeft;
      y += n.offsetTop;
      n = n.offsetParent as HTMLElement | null;
    }
    if (n !== ancestor || el.offsetWidth <= 0) return null;
    return { x, y, w: el.offsetWidth, h: el.offsetHeight };
  }

  /** Grow/shrink animation for the fullscreen map: from/to the minimap's layout rect. */
  function animateMapFull(opening: boolean): Animation | null {
    if (typeof mapFull.animate !== 'function') return null;
    const parent = mapFull.offsetParent as HTMLElement | null;
    const r = parent ? layoutRect(hud.minimapEl, parent) : null;
    const W = mapFull.offsetWidth;
    const H = mapFull.offsetHeight;
    if (!r || W <= 0 || H <= 0) return null;
    const sx = r.w / W;
    const sy = r.h / H;
    const dx = r.x + r.w / 2 - (mapFull.offsetLeft + W / 2);
    const dy = r.y + r.h / 2 - (mapFull.offsetTop + H / 2);
    const small: Keyframe = { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, opacity: 0.6, borderRadius: '24px' };
    const big: Keyframe = { transform: 'translate(0px, 0px) scale(1, 1)', opacity: 1, borderRadius: '0px' };
    return mapFull.animate(opening ? [small, big] : [big, small], {
      duration: 320,
      easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    });
  }

  let mapAnim: Animation | null = null;
  function setMapOpen(open: boolean): Promise<void> {
    if (open === mapOpen) return Promise.resolve();
    mapOpen = open;
    mapAnim?.cancel();
    setClass(rot, 'is-map-open', open);
    if (open) mapFull.hidden = false;
    // The minimap rect is the animation's start/end: measure it while the minimap is laid out
    // (it is display:none while the map is open, so un-hide it first when closing).
    if (!open) hud.setMapOpen(false);
    // Intentionally not gated on prefers-reduced-motion: the user asked for this animation and
    // Android's "remove animations" setting would otherwise silently disable it.
    const anim = (mapAnim = mapFull.hidden ? null : animateMapFull(open));
    if (open) hud.setMapOpen(true);
    const done = (): void => {
      if (mapAnim === anim) mapAnim = null;
      if (!mapOpen) mapFull.hidden = true;
    };
    if (!anim) {
      done();
      return Promise.resolve();
    }
    return anim.finished.then(done, done);
  }

  return {
    overlayRoot: overlay,
    mapSlots: { mini: hud.miniMount, full: mapFull },
    showStart() {
      inSession = false;
      setMapOpen(false);
      applyRotation(0);
      hud.el.hidden = true;
      start.el.hidden = false;
      overlay.dataset.view = 'start';
    },
    showSession() {
      inSession = true;
      start.el.hidden = true;
      hud.el.hidden = false;
      overlay.dataset.view = 'session';
    },
    setStartPhase(phase, message) {
      start.setPhase(phase, message);
    },
    updateHud(state) {
      if (inSession) applyRotation(state.hudRotationDeg);
      hud.update(state, performance.now());
    },
    setMapOpen,
    get isMapOpen() {
      return mapOpen;
    },
    openSettings: () => openSettings(null),
    closeSettings: closeSheets,
    toast,
    get hudRotationDeg() {
      return rotTarget;
    },
    destroy() {
      document.removeEventListener('keydown', onKey);
      hud.dispose();
      start.dispose();
      settings.dispose();
      debug.dispose();
      overlay.remove();
    },
  };
}
